import { useEffect, useRef } from "react";

const LENGTH = 6;
const digitsOnly = (value: string) => value.replace(/\D+/g, "");

/**
 * Six boxes for a TOTP code. Typing moves forward, Backspace moves back, and pasting a code
 * anywhere fills the whole thing. A complete code calls `onComplete`, so the common case is
 * type-six-digits and done.
 */
export function OtpInput({
  value,
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
  label = "Six-digit code",
  invalid = false,
  describedBy,
}: {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  label?: string;
  invalid?: boolean;
  describedBy?: string;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const completed = useRef<string | null>(null);

  useEffect(() => {
    if (value.length === LENGTH) {
      if (completed.current !== value) {
        completed.current = value;
        onComplete?.(value);
      }
    } else {
      completed.current = null;
    }
  }, [value, onComplete]);

  const focusBox = (index: number) => {
    refs.current[Math.max(0, Math.min(LENGTH - 1, index))]?.focus();
  };

  const write = (next: string, caret: number) => {
    const clean = digitsOnly(next).slice(0, LENGTH);
    onChange(clean);
    focusBox(Math.min(caret, LENGTH - 1));
  };

  const onBoxChange = (index: number, raw: string) => {
    const typed = digitsOnly(raw);
    if (!typed) return;
    const chars = value.padEnd(LENGTH, " ").split("");
    for (let i = 0; i < typed.length && index + i < LENGTH; i++) {
      chars[index + i] = typed[i];
    }
    write(chars.join("").trimEnd(), index + typed.length);
  };

  const onKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      if (value[index]) {
        const chars = value.split("");
        chars.splice(index, 1);
        onChange(chars.join(""));
        focusBox(index === value.length - 1 ? index : index);
      } else if (index > 0) {
        const chars = value.split("");
        chars.splice(index - 1, 1);
        onChange(chars.join(""));
        focusBox(index - 1);
      }
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  return (
    <div
      role="group"
      aria-label={label}
      aria-describedby={describedBy}
      style={{ display: "flex", gap: "var(--s-4)" }}
    >
      {Array.from({ length: LENGTH }, (_, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          className="input code-input"
          style={{ letterSpacing: 0, padding: "var(--s-4) 0", minWidth: 0, flex: 1 }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          pattern="[0-9]*"
          maxLength={LENGTH}
          aria-label={`Digit ${index + 1} of ${LENGTH}`}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          value={value[index] ?? ""}
          onChange={(event) => onBoxChange(index, event.target.value)}
          onKeyDown={(event) => onKeyDown(index, event)}
          onFocus={(event) => event.target.select()}
          onPaste={(event) => {
            event.preventDefault();
            const pasted = digitsOnly(event.clipboardData.getData("text"));
            if (pasted) write(pasted, pasted.length);
          }}
        />
      ))}
    </div>
  );
}
